/** Admin-configurable validation rules for terminal flows (Phase 11). */
import { findCatalogStep } from './terminal-flow-catalog.js';
import { isSkip } from './assistant-ticket-new.js';
import { isYes, isNo } from './assistant-ticket-new.js';
import {
  parseSmartDate,
  parseSmartDateTime,
  parseSmartTime,
  validateSmartDate,
  validateSmartDateTime,
  DATE_HINT,
  TIME_HINT,
  DATETIME_HINT,
} from './assistant-datetime.js';

let validationRules = {};

export const VALIDATION_RULE_TYPES = {
  smart_time: {
    label: 'Time (24h)',
    description: 'HHMM or HH:MM',
    params: [
      { key: 'maxHour', label: 'Max hour', type: 'number', min: 0, max: 23, default: 23 },
      { key: 'maxMinute', label: 'Max minute', type: 'number', min: 0, max: 59, default: 59 },
    ],
    hint: () => TIME_HINT,
  },
  smart_date: {
    label: 'Date',
    description: 'YYMMDD or YYYY-MM-DD',
    params: [],
    hint: () => DATE_HINT,
  },
  smart_datetime: {
    label: 'Date + time',
    description: 'YYMMDD HHMM or YYYY-MM-DD HH:MM',
    params: [
      { key: 'maxHour', label: 'Max hour', type: 'number', min: 0, max: 23, default: 23 },
      { key: 'maxMinute', label: 'Max minute', type: 'number', min: 0, max: 59, default: 59 },
    ],
    hint: () => DATETIME_HINT,
  },
  lkr_amount: {
    label: 'LKR amount',
    description: 'Money in Sri Lankan rupees',
    params: [
      { key: 'allowZero', label: 'Allow zero', type: 'boolean', default: false },
      { key: 'min', label: 'Minimum', type: 'number', min: 0, default: 0 },
    ],
    hint: () => 'Amount in LKR',
  },
  integer: {
    label: 'Whole number',
    description: 'Non-negative integer',
    params: [
      { key: 'min', label: 'Minimum', type: 'number', min: 0, default: 0 },
      { key: 'max', label: 'Maximum', type: 'number', min: 0, default: null },
    ],
    hint: () => 'Enter a whole number',
  },
  phone: {
    label: 'Phone',
    description: 'Mobile number with at least N digits',
    params: [
      { key: 'minDigits', label: 'Min digits', type: 'number', min: 7, max: 15, default: 9 },
    ],
    hint: () => '+94 77 123 4567 or 0771234567',
  },
  iata: {
    label: 'Airport code',
    description: '3-letter IATA or N for direct',
    params: [
      { key: 'allowDirect', label: 'Allow N (direct)', type: 'boolean', default: true },
    ],
    hint: () => 'IATA code or N for direct',
  },
  yes_no: {
    label: 'Yes / No',
    description: 'Y or N confirmation',
    params: [],
    hint: () => 'Y or N',
  },
  text: {
    label: 'Text',
    description: 'Non-empty text when required',
    params: [
      { key: 'minLength', label: 'Min length', type: 'number', min: 0, default: 1 },
    ],
    hint: () => 'Text answer',
  },
  optional_text: {
    label: 'Optional text',
    description: 'Text or skip with -',
    params: [
      { key: 'minLength', label: 'Min length when provided', type: 'number', min: 0, default: 0 },
    ],
    hint: () => 'Skip with - if none',
  },
  numbered_choice: {
    label: 'Numbered choice',
    description: 'Pick 1–N from list or type value',
    params: [
      { key: 'choiceCount', label: 'Choice count', type: 'number', min: 2, max: 12, default: 2 },
    ],
    hint: () => 'Pick a number from the list',
  },
  locked: {
    label: 'Locked in code',
    description: 'Complex validation — view only',
    params: [],
    hint: () => '',
  },
};

const LOCKED_STEP_KEYS = new Set([
  'pref_transit',
  'pref_trip_type',
  'pref_baggage',
  'pref_seat',
  'source',
  'passenger_summary',
  'add_passenger_type',
  'traveler:*:*:same_fare',
  'traveler:*:*:fare_confirm',
  'shared:*:confirm',
  'seats_booked',
]);

const HANDLER_RULE_MAP = {
  text_optional: 'optional_text',
  text_required: 'text',
  lkr_amount_optional: 'lkr_amount',
  date_optional: 'smart_date',
  numbered_choice: 'numbered_choice',
};

function wildcardToRegExp(pattern) {
  const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`^${escaped.replace(/\\\*/g, '[^:]+')}$`);
}

export function validationStorageKey(flowId, stepKey) {
  return `${flowId}.${stepKey}`;
}

export function setTerminalFlowValidationRules(obj) {
  validationRules = obj && typeof obj === 'object' ? obj : {};
}

export function getTerminalFlowValidationRules() {
  return validationRules;
}

function lookupValidationOverride(flowId, stepKey) {
  const exact = validationStorageKey(flowId, stepKey);
  if (validationRules[exact]) return validationRules[exact];

  const prefix = `${flowId}.`;
  for (const [key, val] of Object.entries(validationRules)) {
    if (!key.startsWith(prefix)) continue;
    const pattern = key.slice(prefix.length);
    if (!pattern.includes('*')) continue;
    if (wildcardToRegExp(pattern).test(stepKey)) return val;
  }
  return null;
}

function defaultParams(type) {
  const spec = VALIDATION_RULE_TYPES[type];
  if (!spec?.params?.length) return {};
  const out = {};
  spec.params.forEach((p) => {
    if (p.default != null) out[p.key] = p.default;
  });
  return out;
}

function countChoices(step, catalogStep) {
  const raw = catalogStep?.choices || step?.hint || '';
  const lines = String(raw).split(/[·\n]/).map((s) => s.trim()).filter(Boolean);
  return Math.max(lines.length, 2);
}

/** Infer default validation rule from step key / catalog / custom handler. */
export function inferValidationRule(stepKey, catalogStep = null, step = null) {
  const key = String(stepKey || '');

  if (step?._handler && HANDLER_RULE_MAP[step._handler]) {
    const type = HANDLER_RULE_MAP[step._handler];
    const params = defaultParams(type);
    if (type === 'lkr_amount') params.allowZero = step.optional !== false;
    if (type === 'numbered_choice') params.choiceCount = countChoices(step, catalogStep);
    return {
      type,
      required: step.optional ? false : true,
      editable: true,
      params,
      lockedReason: null,
    };
  }

  for (const locked of LOCKED_STEP_KEYS) {
    if (locked.includes('*') ? wildcardToRegExp(locked).test(key) : locked === key) {
      return {
        type: 'locked',
        required: true,
        editable: false,
        params: {},
        lockedReason: 'Routing, confirm loops, or multi-field logic stay in code.',
      };
    }
  }

  if (/depart_time|arrive_time|transit_layover|:time$/.test(key)) {
    return { type: 'smart_time', required: true, editable: true, params: defaultParams('smart_time'), lockedReason: null };
  }
  if (/booking_expires|pay_before|remain_pay_by|payment_date/.test(key)) {
    return { type: 'smart_datetime', required: true, editable: true, params: defaultParams('smart_datetime'), lockedReason: null };
  }
  if (/quote_valid_till|depart_date|return_date|_date|date_from|date_to|travel_date|followup|applied|issued|booked|advance_date|balance_collect|_on$/.test(key)) {
    const optional = catalogStep?.optional || step?.optional;
    return {
      type: 'smart_date',
      required: !optional,
      editable: true,
      params: {},
      lockedReason: null,
    };
  }
  if (/^pax_adt$/.test(key)) {
    return {
      type: 'integer',
      required: true,
      editable: false,
      params: { min: 0, max: 99 },
      lockedReason: 'ADT/STU cross-check (0 ADT when STU>0) stays in code.',
    };
  }
  if (/^pax_/.test(key)) {
    return {
      type: 'integer',
      required: false,
      editable: true,
      params: { min: 0, max: 99 },
      lockedReason: null,
    };
  }
  if (/phone/.test(key)) {
    return { type: 'phone', required: true, editable: true, params: defaultParams('phone'), lockedReason: null };
  }
  if (/transit_stop|transit_via|pref_transit_via/.test(key)) {
    return { type: 'iata', required: true, editable: true, params: defaultParams('iata'), lockedReason: null };
  }
  if (/cost|profit|advance|payment_amount|budget|final_quote|agent_total|quote_price|amount|seat_fee|sell_|rate_|_total|flight_fare|visa_fee|profit_markup/.test(key)) {
    const optional = catalogStep?.optional || step?.optional;
    return {
      type: 'lkr_amount',
      required: !optional,
      editable: true,
      params: { ...defaultParams('lkr_amount'), allowZero: optional || /advance|payment_amount/.test(key) },
      lockedReason: null,
    };
  }
  if (/passenger_summary|:same_fare|:confirm$|seats_booked/.test(key)) {
    return { type: 'yes_no', required: true, editable: false, params: {}, lockedReason: 'Confirm / branch logic stays in code.' };
  }
  if (/pref_baggage|pref_seat|package|purpose|duration|source|add_passenger|call_via|reason/.test(key)) {
    return {
      type: 'numbered_choice',
      required: !catalogStep?.optional,
      editable: true,
      params: { choiceCount: countChoices(step, catalogStep) },
      lockedReason: null,
    };
  }
  if (/email|note|requirements|ticket_details|airline|ticket_class|custom_/.test(key) || catalogStep?.optional || step?.optional) {
    return {
      type: 'optional_text',
      required: false,
      editable: true,
      params: defaultParams('optional_text'),
      lockedReason: null,
    };
  }
  if (/name|passport|pnr|origin|destination|package/.test(key)) {
    return {
      type: 'text',
      required: true,
      editable: true,
      params: defaultParams('text'),
      lockedReason: null,
    };
  }

  return {
    type: 'text',
    required: !(catalogStep?.optional || step?.optional),
    editable: true,
    params: defaultParams('text'),
    lockedReason: null,
  };
}

function mergeParams(type, base = {}, patch = {}) {
  const spec = VALIDATION_RULE_TYPES[type];
  const out = { ...base };
  if (!spec || !patch || typeof patch !== 'object') return out;
  spec.params.forEach((p) => {
    if (patch[p.key] == null || patch[p.key] === '') return;
    if (p.type === 'boolean') out[p.key] = !!patch[p.key];
    else {
      const n = Number(patch[p.key]);
      if (Number.isFinite(n)) out[p.key] = n;
    }
  });
  return out;
}

export function resolveValidationRule(flowId, stepKey, catalogStep = null, step = null) {
  const inferred = inferValidationRule(stepKey, catalogStep, step);
  const override = lookupValidationOverride(flowId, stepKey);
  if (!override) return inferred;
  const type = override.type && VALIDATION_RULE_TYPES[override.type] ? override.type : inferred.type;
  return {
    ...inferred,
    type,
    required: override.required != null ? !!override.required : inferred.required,
    params: mergeParams(type, inferred.params, override.params),
    hasOverride: true,
  };
}

function parseLkrAmount(v) {
  const n = Number(String(v ?? '').trim().replace(/,/g, ''));
  return Number.isFinite(n) ? n : NaN;
}

function buildTimeOpts(params = {}) {
  return {
    maxHour: Number.isFinite(params.maxHour) ? params.maxHour : 23,
    maxMinute: Number.isFinite(params.maxMinute) ? params.maxMinute : 59,
  };
}

export function describeValidationRule(rule) {
  if (!rule) return 'Unknown';
  const spec = VALIDATION_RULE_TYPES[rule.type];
  if (!spec) return rule.type;
  if (rule.type === 'locked') return rule.lockedReason || spec.description;
  const req = rule.required ? 'Required' : 'Optional';
  const paramBits = (spec.params || [])
    .map((p) => {
      const val = rule.params?.[p.key];
      if (val == null || val === '') return null;
      if (p.type === 'boolean') return val ? p.label : null;
      return `${p.label}: ${val}`;
    })
    .filter(Boolean);
  return [spec.label, req, ...paramBits].filter(Boolean).join(' · ');
}

export function buildValidator(rule, step = {}) {
  const optional = step.optional != null ? !!step.optional : !rule.required;
  const params = rule.params || {};

  if (rule.type === 'locked') {
    return step.validate || (() => null);
  }

  if (rule.type === 'smart_time') {
    const opts = buildTimeOpts(params);
    const hint = `Invalid time. Use HHMM or HH:MM up to ${String(opts.maxHour).padStart(2, '0')}:${String(opts.maxMinute).padStart(2, '0')}.`;
    return (v) => {
      if (optional && isSkip(v)) return null;
      return parseSmartTime(v, opts) ? null : hint;
    };
  }

  if (rule.type === 'smart_date') {
    return (v) => {
      if (optional && isSkip(v)) return null;
      if (!rule.required && isSkip(v)) return null;
      return validateSmartDate(v, { required: !optional && rule.required });
    };
  }

  if (rule.type === 'smart_datetime') {
    const opts = buildTimeOpts(params);
    return (v) => {
      if (optional && isSkip(v)) return null;
      if (!parseSmartDateTime(v, opts)) return `Invalid date/time. Use ${DATETIME_HINT}.`;
      return null;
    };
  }

  if (rule.type === 'lkr_amount') {
    const allowZero = !!params.allowZero;
    const min = Number.isFinite(params.min) ? params.min : 0;
    return (v) => {
      if (optional && isSkip(v)) return null;
      const n = parseLkrAmount(v);
      if (!Number.isFinite(n) || n < min) return 'Enter amount in LKR.';
      if (!allowZero && n <= 0) return 'Enter amount in LKR.';
      return null;
    };
  }

  if (rule.type === 'integer') {
    const min = Number.isFinite(params.min) ? params.min : 0;
    const max = Number.isFinite(params.max) ? params.max : null;
    return (v) => {
      if (optional && isSkip(v)) return null;
      const n = Number(String(v).trim());
      if (!Number.isInteger(n)) return `Enter a whole number${max != null ? ` (${min}–${max})` : ` (min ${min})`}.`;
      if (n < min || (max != null && n > max)) return `Enter a whole number${max != null ? ` (${min}–${max})` : ` (min ${min})`}.`;
      return null;
    };
  }

  if (rule.type === 'phone') {
    const minDigits = Number.isFinite(params.minDigits) ? params.minDigits : 9;
    return (v) => {
      const digits = String(v || '').replace(/\D/g, '');
      if (digits.length < minDigits) return `Enter a valid phone (at least ${minDigits} digits).`;
      return null;
    };
  }

  if (rule.type === 'iata') {
    const allowDirect = params.allowDirect !== false;
    return (v) => {
      const s = String(v || '').trim().toUpperCase();
      if (allowDirect && (s === 'N' || s === 'NONE' || s === '-')) return null;
      if (/^[A-Z]{3}$/.test(s)) return null;
      return 'Enter a 3-letter IATA code' + (allowDirect ? ' or N for direct.' : '.');
    };
  }

  if (rule.type === 'yes_no') {
    return (v) => ((isYes(v) || isNo(v)) ? null : 'Reply Y or N.');
  }

  if (rule.type === 'optional_text') {
    const minLength = Number.isFinite(params.minLength) ? params.minLength : 0;
    return (v) => {
      if (optional && isSkip(v)) return null;
      const s = String(v || '').trim();
      if (s.length < minLength) return minLength > 0 ? `Enter at least ${minLength} characters or skip.` : 'Enter text or skip with -.';
      return null;
    };
  }

  if (rule.type === 'text') {
    const minLength = Number.isFinite(params.minLength) ? params.minLength : 1;
    return (v) => {
      if (String(v || '').trim().length < minLength) return minLength > 1 ? `Enter at least ${minLength} characters.` : 'Required.';
      return null;
    };
  }

  if (rule.type === 'numbered_choice') {
    const count = Number.isFinite(params.choiceCount) ? params.choiceCount : 2;
    return (v) => {
      if (optional && isSkip(v)) return null;
      const n = Number(String(v).trim());
      if (Number.isInteger(n) && n >= 1 && n <= count) return null;
      if (String(v).trim().length >= 1) return null;
      return `Pick 1–${count} or type a value.`;
    };
  }

  return step.validate || (() => null);
}

export function buildParser(rule, step = {}) {
  const optional = step.optional != null ? !!step.optional : !rule.required;
  const params = rule.params || {};

  if (rule.type === 'locked') return step.parse || ((v) => v);

  if (rule.type === 'smart_time') {
    const opts = buildTimeOpts(params);
    return (v) => (optional && isSkip(v) ? null : parseSmartTime(v, opts));
  }
  if (rule.type === 'smart_date') {
    return (v) => (optional && isSkip(v) ? null : parseSmartDate(v));
  }
  if (rule.type === 'smart_datetime') {
    const opts = buildTimeOpts(params);
    return (v) => (optional && isSkip(v) ? null : parseSmartDateTime(v, opts));
  }
  if (rule.type === 'lkr_amount') {
    return (v) => (optional && isSkip(v) ? null : Math.round(parseLkrAmount(v)));
  }
  if (rule.type === 'integer') {
    return (v) => (optional && isSkip(v) ? null : Number(String(v).trim()));
  }
  if (rule.type === 'phone') {
    return (v) => String(v || '').trim();
  }
  if (rule.type === 'iata') {
    return (v) => {
      const s = String(v || '').trim().toUpperCase();
      if (params.allowDirect !== false && (s === 'N' || s === 'NONE' || s === '-')) return null;
      return s;
    };
  }
  if (rule.type === 'yes_no') {
    return (v) => v;
  }
  if (rule.type === 'optional_text' || rule.type === 'text') {
    return (v) => (optional && isSkip(v) ? null : String(v).trim());
  }
  if (rule.type === 'numbered_choice') {
    const count = Number.isFinite(params.choiceCount) ? params.choiceCount : 2;
    return (v) => {
      if (optional && isSkip(v)) return null;
      const n = Number(String(v).trim());
      if (Number.isInteger(n) && n >= 1 && n <= count) return String(n);
      return String(v).trim();
    };
  }

  return step.parse || ((v) => v);
}

/** Apply stored validation rules to runtime steps (after copy overrides). */
export function applyFlowValidationRules(flowId, steps) {
  if (!flowId || !Array.isArray(steps) || !steps.length) return steps;
  return steps.map((step) => {
    const catalog = findCatalogStep(flowId, step.key);
    const rule = resolveValidationRule(flowId, step.key, catalog, step);
    if (!rule.editable && !rule.hasOverride) {
      return { ...step, _validationRule: rule };
    }
    const next = {
      ...step,
      _validationRule: rule,
      validate: buildValidator(rule, step),
      parse: buildParser(rule, step),
    };
    return next;
  });
}

export function listFlowValidationSummary(flowId, catalogSteps = []) {
  return catalogSteps.map((catalogStep) => {
    const rule = resolveValidationRule(flowId, catalogStep.key, catalogStep);
    return {
      key: catalogStep.key,
      label: catalogStep.label || catalogStep.key,
      rule,
      description: describeValidationRule(rule),
      customized: !!rule.hasOverride,
    };
  });
}

export function sanitizeValidationOverride(form, rule) {
  if (!rule?.editable) return null;
  const patch = {};
  const spec = VALIDATION_RULE_TYPES[rule.type];
  if (!spec) return null;
  if (form.required != null) patch.required = !!form.required;
  const params = {};
  (spec.params || []).forEach((p) => {
    const raw = form.params?.[p.key];
    if (raw == null || raw === '') return;
    params[p.key] = p.type === 'boolean' ? !!raw : Number(raw);
  });
  if (Object.keys(params).length) patch.params = params;
  if (!Object.keys(patch).length) return null;
  return patch;
}

export function validationOverrideDiffersFromDefault(flowId, stepKey, catalogStep, patch) {
  if (!patch) return false;
  const base = inferValidationRule(stepKey, catalogStep);
  const merged = resolveValidationRule(flowId, stepKey, catalogStep);
  merged.params = mergeParams(merged.type, base.params, patch.params || {});
  if (patch.required != null) merged.required = !!patch.required;
  const resolved = resolveValidationRule(flowId, stepKey, catalogStep);
  resolved.params = mergeParams(resolved.type, base.params, patch.params || {});
  if (patch.required != null) resolved.required = !!patch.required;
  return JSON.stringify({ required: merged.required, params: merged.params })
    !== JSON.stringify({ required: base.required, params: base.params });
}

export function handlerTemplateForRule(type) {
  return Object.entries(HANDLER_RULE_MAP).find(([, t]) => t === type)?.[0] || null;
}
